export type LoginDisclaimerAudience = 'seller' | 'storefront'

type QueuedLoginDisclaimer = {
  audience: LoginDisclaimerAudience
  queuedAt: number
}

const LOGIN_DISCLAIMER_STORAGE_KEY = 'trendify-login-disclaimer'

export const loginDisclaimerContent = {
  title: 'Welcome - Development Environment Notice',
  intro:
    'This environment is for development and testing only.',
  highlights: [
    'Order, checkout, payment, support, and related business activity shown here is dummy workflow data used for testing.',
    'The payment integration is connected to a real gateway flow, so do not use real customer, seller, or card details here.',
    'Enter only dummy names, phone numbers, addresses, and business information while testing.',
    'When an email address is needed, use disposable inboxes such as MailSec or Yopmail.',
  ],
  closing:
    'Continue only if you are testing with non-production data.',
  confirmLabel: 'Continue Testing',
} as const

const parseQueuedDisclaimer = (rawValue: string | null): QueuedLoginDisclaimer | null => {
  if (!rawValue) {
    return null
  }

  try {
    const parsed = JSON.parse(rawValue) as Partial<QueuedLoginDisclaimer>

    if (
      (parsed.audience === 'seller' || parsed.audience === 'storefront') &&
      typeof parsed.queuedAt === 'number'
    ) {
      return {
        audience: parsed.audience,
        queuedAt: parsed.queuedAt,
      }
    }
  } catch {
    // Ignore malformed storage payloads and clear them below.
  }

  return null
}

export const queueLoginDisclaimer = (audience: LoginDisclaimerAudience) => {
  if (typeof window === 'undefined') {
    return
  }

  const payload: QueuedLoginDisclaimer = {
    audience,
    queuedAt: Date.now(),
  }

  window.localStorage.setItem(LOGIN_DISCLAIMER_STORAGE_KEY, JSON.stringify(payload))
}

export const consumeQueuedLoginDisclaimer = (
  audience: LoginDisclaimerAudience,
): QueuedLoginDisclaimer | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const payload = parseQueuedDisclaimer(
    window.localStorage.getItem(LOGIN_DISCLAIMER_STORAGE_KEY),
  )

  if (!payload) {
    window.localStorage.removeItem(LOGIN_DISCLAIMER_STORAGE_KEY)
    return null
  }

  if (payload.audience !== audience) {
    return null
  }

  window.localStorage.removeItem(LOGIN_DISCLAIMER_STORAGE_KEY)
  return payload
}
